"use client";

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Globe, CheckCircle, AlertCircle, Copy, Loader2 } from 'lucide-react';
import { showSuccess, showError } from '@/utils/toast';
import { Alert, AlertDescription } from "@/components/ui/alert";

const DNSManagement = () => {
  const { profile, company } = useAuth();
  const [domain, setDomain] = useState(company?.custom_domain || '');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  // Verify DNS mutation
  const verifyMutation = useMutation({
    mutationFn: async (domainToVerify: string) => {
      const { data, error } = await supabase.functions.invoke('verify-dns', {
        body: { domain: domainToVerify },
      });
      if (error) throw error;
      if (!data?.valid) throw new Error(data?.error || 'DNS verification failed');
      return data;
    },
    onSuccess: (result) => {
      setVerificationResult(result);
      showSuccess(`DNS verified! Record: ${result.record}`);
      // Update company with verified status
      if (company?.id) {
        supabase
          .from('companies')
          .update({ custom_domain: domain, domain_verified: true })
          .eq('id', company.id);
      }
    },
    onError: (error) => {
      setVerificationResult({ valid: false, error: error.message });
      showError(error.message || 'DNS verification failed');
    },
  });

  const copyRecord = () => {
    navigator.clipboard.writeText(verificationResult.record || '');
    showSuccess('DNS record copied to clipboard!');
  };

  if (!company?.id || profile?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Access denied. Admin only.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">DNS Management</h1>
      
      <Card>
        <CardHeader>
          <CardTitle>Custom Domain Setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="domain">Custom Domain</Label>
            <Input
              id="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
            />
            <p className="text-sm text-muted-foreground">
              Enter your custom domain (e.g., yourcompany.com). We'll verify DNS records.
            </p>
          </div>
          <Button 
            onClick={() => verifyMutation.mutate(domain)} 
            disabled={!domain || isVerifying}
            className="flex items-center space-x-2"
          >
            {isVerifying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Verifying...
              </>
            ) : (
              <>
                <Globe className="h-4 w-4 mr-2" />
                Verify DNS
              </>
            )}
          </Button>
          
          {verificationResult && (
            <div className={`p-4 rounded-lg ${verificationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-destructive/10 border border-destructive/20'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium">
                  {verificationResult.valid ? (
                    <CheckCircle className="h-4 w-4 text-green-500 inline mr-2" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-destructive inline mr-2" />
                  )}
                  Status: {verificationResult.valid ? 'Verified' : 'Failed'}
                </div>
                {verificationResult.record && (
                  <Button variant="ghost" size="sm" onClick={copyRecord}>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy Record
                  </Button>
                )}
              </div>
              {verificationResult.record && (
                <div className="text-sm bg-muted/20 p-2 rounded">
                  <strong>Required DNS Record:</strong><br />
                  <code className="font-mono text-xs break-all">{verificationResult.record}</code>
                </div>
              )}
              {verificationResult.error && (
                <div className="text-sm text-destructive mt-2">
                  Error: {verificationResult.error}
                </div>
              )}
            </div>
          )}

          {company?.domain_verified && (
            <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <div>
                <div className="font-medium">Domain Verified!</div>
                <div className="text-sm text-muted-foreground">Your custom domain is active.</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SSL Certificate</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground">
            SSL certificates are automatically provisioned for verified domains via Let's Encrypt.
          </div>
          {company?.domain_verified ? (
            <Badge variant="default">SSL Active</Badge>
          ) : (
            <Badge variant="secondary">SSL Pending</Badge>
          )}
          <Button variant="outline" disabled={!company?.domain_verified}>
            Renew SSL Certificate
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>DNS Records Guide</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="space-y-2">
            <h4 className="font-medium">Required Records:</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>CNAME:</strong> <code>www.yourdomain.com</code> → <code>wightwayconnect.uk</code></li>
              <li><strong>CNAME:</strong> <code>*.yourdomain.com</code> → <code>wightwayconnect.uk</code></li>
              <li><strong>A Record:</strong> <code>yourdomain.com</code> → <code>76.76.21.21</code> (Vercel IP)</li>
              <li><strong>MX Record:</strong> <code>yourdomain.com</code> → <code>mx1.supabase.com</code> (for email)</li>
            </ul>
            <p className="text-xs text-muted-foreground">
              Update these records with your DNS provider (Cloudflare, GoDaddy, etc.). Verification takes 5-15 minutes.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DNSManagement;